export interface AdminMembers {
  id: number;
  role: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
}

export interface PostageRate {
  envelopeType: string;
  id: number;
  postageType: string;
  price: number;
  tariffType: string;
  weight: number;
}

export interface PostageRateV2 {
  id: number;
  price: number;
  weightNotOverOz: number;
  mailingDestinationType: string;
  mailingType: string;
  packageType: string;
  sheetLimit: number | null;
}

export interface DocProcessingRate {
  id: number;
  name: string;
  price: number;
  pricingUnit: 'per_page' | 'per_address' | 'per_order';
}

export interface MeteredRate {
  id: number;
  name: 'paf_discount' | 'cert_err';
  price: number;
}

export interface CriticalWeightInput {
  id: number;
  name: string;
  weight: number;
}

export interface DocProcessingSheet {
  id: number;
  isSystem: boolean;
  name: string;
}

export interface PriceSheet {
  id: number;
  name: string;
  isSystem: boolean;
}

export interface Customer {
  domains: { id: number; name: string }[];
  id: number;
  name: string;
  sheetId: number;
  type: string;
  sheet: {
    id: number;
    name: string;
  };
  printCredits: number;
}

export interface PromoCode {
  createdAt: string;
  endAt: string;
  id: number;
  isActivated: boolean;
  name: string;
  printCreditAmount: number;
  printCreditEndAt: string;
  startAt: string;
}

export interface Admin {
  members: AdminMembers[];
  fetchError: string;
  customers: Customer[];
  priceSheets: PriceSheet[];
  promoCodes: PromoCode[];
  promoCodeForm: {
    name: string;
    startAt: string;
    endAt: string;
    printCreditEndAt: string;
    printCreditAmount: string;
  };
  isAddPromoCodeModalOpen: boolean;
  isLoadingPromoCodes: boolean;
  fetchOrganizationsError: string;
  isFetchingOrganizations: boolean;
  isFetchingSheetPrices: boolean;
  postageRates: PostageRate[];
  sheetPricePayload: {
    printPriceCosts: { id: number; unitPrice: number }[];
    printPriceAdjustments: { id: number; unitPrice: number; thresholdSize?: number }[];
  };
  transformedSheetPriceData: any;
  transformedPostageRates: any;
  editOrganizationError: Record<string, boolean>;
  isChangingJobStatus: boolean;
  isUpdatingJobTrackingNumbers: boolean;
  sortedPostageRatesV2: {
    first_class: Record<number, PostageRateV2[]>;
    certified: Record<number, PostageRateV2[]>;
    priority_express: Record<number, PostageRateV2[]>;
  };
  postageRatesResponse: PostageRateV2[];
  updatedEnvelopeRates: { [key: number] : number };
  unsavedPostageRates: { [key: number]: string };
  documentProcessingRates: Record<string, DocProcessingRate[]>;
  unsavedDocProcessingRatesChanges: { [key: string]: string };
  meteredRates: { cert_err: number; paf_discount: number };
  unsavedMeteredInputs: { [key: string]: string };
  weightCriticalInput: CriticalWeightInput[];
  unsavedWeightChanges: { [key: string]: string };
  docSheets: DocProcessingSheet[];
}

export const initialState: Admin = {
  members: [],
  fetchError: '',
  customers: [],
  priceSheets: [],
  fetchOrganizationsError: '',
  transformedPostageRates: {},
  postageRates: [],
  promoCodes: [],
  promoCodeForm: {
    name: '',
    startAt: '',
    endAt: '',
    printCreditEndAt: '',
    printCreditAmount: '',
  },
  isAddPromoCodeModalOpen: false,
  isLoadingPromoCodes: false,
  transformedSheetPriceData: {},
  sheetPricePayload: { printPriceCosts: [], printPriceAdjustments: [] },
  isFetchingOrganizations: false,
  isFetchingSheetPrices: false,
  editOrganizationError: {},
  isChangingJobStatus: false,
  isUpdatingJobTrackingNumbers: false,
  sortedPostageRatesV2: {
    first_class: {},
    certified: {},
    priority_express: {},
  },
  postageRatesResponse: [],
  updatedEnvelopeRates: {},
  documentProcessingRates: { per_page: [], per_address: [], per_order: [] },
  meteredRates: { cert_err: 0, paf_discount: 0 },
  unsavedPostageRates: {},
  unsavedMeteredInputs: {},
  weightCriticalInput: [],
  unsavedWeightChanges: {},
  unsavedDocProcessingRatesChanges: {},
  docSheets: [],
};
